<template>
  <v-container fluid class="exp-page">
    <section class="exp-container">
      <!-- Month Filter & Overview Header Bar -->
      <app-card-title
        title="expense_category"
        @filter="filterMonth"
      />

      <KpiCards class="mb-4" :items="kpiItems" :loading="loadingSummary" :skeleton="4" />

      <!-- Chart & Breakdown Ranking Card -->
      <v-card class="exp-main-card mb-4" flat>
        <v-card-text class="pa-4">
          <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
            <div class="d-flex align-center">
              <span class="exp-title-accent mr-2"></span>
              <div>
                <h2 class="exp-page-title mb-0">{{ $t("expense_chart") }}</h2>
                <span class="exp-item-count">
                  {{ $t("expense_category") }} ({{ currentMonthDisplay }})
                </span>
              </div>
            </div>
            <v-chip
              small
              outlined
              color="primary"
              class="font-weight-bold mt-2 mt-sm-0"
            >
              <v-icon left x-small>fal fa-calendar-alt</v-icon>
              {{ currentMonthDisplay }}
            </v-chip>
          </div>

          <v-row v-if="chartSeries && chartSeries.length > 0" align="center">
            <!-- Left: ApexCharts Donut Chart -->
            <v-col cols="12" md="6" class="d-flex justify-center">
              <div class="exp-chart-wrapper w-100">
                <apexchart
                  type="donut"
                  width="100%"
                  height="330"
                  :options="donutChartOptions"
                  :series="chartSeries"
                />
              </div>
            </v-col>

            <!-- Right: Breakdown Ranking List -->
            <v-col cols="12" md="6">
              <div class="exp-breakdown-panel">
                <div class="exp-breakdown-header mb-3 d-flex align-center justify-space-between">
                  <span class="font-weight-bold text-caption text-uppercase grey--text">
                    {{ $t("expense_breakdown") }}
                  </span>
                  <span class="text-caption grey--text">
                    {{ breakdownItems.length }} {{ $t("category") }}
                  </span>
                </div>
                <div class="exp-breakdown-list">
                  <div
                    v-for="(cat, idx) in breakdownItems"
                    :key="cat.id"
                    class="exp-breakdown-row mb-2 pa-2 rounded-lg"
                  >
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="d-flex align-center min-w-0 flex-grow-1 mr-2">
                        <span
                          class="exp-rank-badge mr-2 font-weight-bold"
                          :style="{ backgroundColor: getChartColor(idx) + '22', color: getChartColor(idx) }"
                        >
                          {{ idx + 1 }}
                        </span>
                        <span class="font-weight-medium text-truncate exp-breakdown-name">
                          {{ cat.name }}
                        </span>
                        <v-chip x-small class="ml-2 font-weight-bold" color="grey lighten-3" text-color="grey darken-2">
                          {{ cat.transaction_count || 0 }} {{ $t("items") }}
                        </v-chip>
                      </div>
                      <div class="text-right flex-shrink-0">
                        <span class="exp-price-badge exp-price-badge--expense font-weight-bold">
                          -{{ cat.total_price | NumberFormat }} <span class="exp-price-currency">LAK</span>
                        </span>
                        <span class="text-caption grey--text ml-1 font-weight-bold">
                          ({{ cat.percentage }}%)
                        </span>
                      </div>
                    </div>
                    <v-progress-linear
                      :value="cat.percentage"
                      height="6"
                      rounded
                      :color="getChartColor(idx)"
                      background-color="grey lighten-3"
                    />
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Empty Chart State -->
          <div v-else class="text-center py-8">
            <v-icon size="48" color="grey lighten-1">fal fa-chart-pie</v-icon>
            <div class="mt-2 text-body-1 grey--text text--darken-1 font-weight-medium">
              {{ $t("no_expense_data") }}
            </div>
            <div class="text-caption grey--text">
              {{ currentMonthDisplay }}
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Main Category Records Panel -->
      <v-card class="exp-main-card" flat>
        <v-card-text class="pa-4">
          <!-- Toolbar -->
          <div class="d-flex align-center flex-wrap exp-toolbar mb-4">
            <div class="d-flex align-center exp-title-group mr-3">
              <span class="exp-title-accent mr-2"></span>
              <div>
                <h1 class="exp-page-title mb-0">{{ $t("category") }}</h1>
                <span class="exp-item-count" v-if="categoryPagination">
                  {{ categoryPagination.total || 0 }} {{ $t("items") }}
                </span>
              </div>
            </div>

            <!-- Search Field & Actions -->
            <div class="d-flex align-center flex-wrap exp-toolbar-actions ml-auto mt-2 mt-sm-0">
              <div class="exp-search-box mr-2">
                <TextSearch
                  v-model="search"
                  @search="fetchItem(1)"
                  @onClear="
                    search = '';
                    fetchItem(1);
                  "
                />
              </div>

              <!-- Back to Expenses Button -->
              <v-btn
                outlined
                depressed
                class="exp-btn-secondary mr-2"
                to="/expense"
              >
                <v-icon left small>fal fa-arrow-left</v-icon>
                {{ $t("back_to_expense") }}
              </v-btn>

              <!-- New Category Button -->
              <v-btn
                depressed
                class="exp-btn-primary"
                @click="newItem"
              >
                <v-icon left small>fal fa-plus</v-icon>
                {{ $t("new_data") }}
              </v-btn>
            </div>
          </div>

          <!-- Data Table -->
          <v-data-table
            :headers="headers"
            :items="categoryItems"
            :loading="isLoading"
            :loading-text="$t('loading')"
            :no-data-text="$t('no_data')"
            hide-default-footer
            class="exp-table elevation-0"
          >
            <!-- Index Column -->
            <template v-slot:item.index="{ index }">
              <span class="exp-col-index">
                {{ ((categoryPagination.current_page || 1) - 1) * (categoryPagination.per_page || 15) + index + 1 }}
              </span>
            </template>

            <!-- Name Column -->
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center py-1">
                <v-icon size="14" color="primary" class="mr-2">fal fa-tag</v-icon>
                <span class="font-weight-medium exp-item-name">{{ item.name }}</span>
              </div>
            </template>

            <!-- Total Expense Column -->
            <template v-slot:item.expenses_sum_price="{ item }">
              <span
                v-if="item.expenses_sum_price && Number(item.expenses_sum_price) > 0"
                class="exp-price-badge exp-price-badge--expense font-weight-bold"
              >
                -{{ item.expenses_sum_price | NumberFormat }}
                <span class="exp-price-currency">LAK</span>
              </span>
              <span v-else class="grey--text text-caption font-weight-medium">
                0 LAK
              </span>
            </template>

            <!-- Expense Count Column -->
            <template v-slot:item.expenses_count="{ item }">
              <v-chip
                x-small
                outlined
                :color="item.expenses_count > 0 ? 'primary' : 'grey'"
                class="font-weight-bold"
              >
                {{ item.expenses_count || 0 }} {{ $t("items") }}
              </v-chip>
            </template>

            <!-- Date Column -->
            <template v-slot:item.created_at="{ item }">
              <span class="text-caption grey--text text--darken-1">
                {{ item.created_at | moment }}
              </span>
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center justify-end">
                <v-btn icon small color="info" @click="viewItem(item)" :title="$t('view')">
                  <v-icon small>fal fa-eye</v-icon>
                </v-btn>
                <v-btn icon small color="primary" @click="editItem(item)" :title="$t('edit')">
                  <v-icon small>fal fa-pencil</v-icon>
                </v-btn>
                <v-btn icon small color="error" @click="deleteItem(item)" :title="$t('delete')">
                  <v-icon small>fal fa-trash-alt</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>

          <!-- Pagination Footer -->
          <div
            v-if="categoryPagination.last_page > 1"
            class="d-flex align-center justify-space-between flex-wrap pt-4 px-2"
          >
            <span class="text-caption grey--text">
              {{ $t("total") }}: {{ categoryPagination.total || 0 }} {{ $t("items") }}
            </span>
            <v-pagination
              :value="categoryPagination.current_page || 1"
              :length="categoryPagination.last_page || 1"
              :total-visible="7"
              color="primary"
              @input="onPageChange"
            />
          </div>
        </v-card-text>
      </v-card>

      <!-- View Item Details Dialog -->
      <app-custom-dialog
        v-model="dialog"
        :footer="false"
        title="description"
      >
        <div slot="activator">
          <v-row>
            <v-col cols="4" class="grey--text">{{ $t("id") }}</v-col>
            <v-col cols="8" class="font-weight-medium">{{ form.id }}</v-col>

            <v-col cols="4" class="grey--text">{{ $t("name") }}</v-col>
            <v-col cols="8" class="font-weight-medium">{{ form.name }}</v-col>

            <v-col cols="4" class="grey--text">{{ $t("total_expense") }}</v-col>
            <v-col cols="8" class="font-weight-bold red--text">
              {{ (form.expenses_sum_price || 0) | NumberFormat }} LAK
            </v-col>

            <v-col cols="4" class="grey--text">{{ $t("expense_count") }}</v-col>
            <v-col cols="8">{{ form.expenses_count || 0 }} {{ $t("items") }}</v-col>

            <v-col cols="4" class="grey--text">{{ $t("date") }}</v-col>
            <v-col cols="8">{{ form.created_at | moment }}</v-col>
          </v-row>
        </div>
      </app-custom-dialog>

      <!-- Add/Edit Category Dialog -->
      <app-custom-dialog
        v-model="dialogAdd"
        :footer="true"
        :title="titleStatus"
        :submit-text="update ? 'save' : 'add'"
        :submit-loading="submitting"
        @submit="submitForm"
      >
        <div slot="activator">
          <TTextField
            :errorMessages="nameError"
            label="name"
            v-model="form.name"
            @input="$v.form.name.$touch()"
            @blur="$v.form.name.$touch()"
          />
        </div>
      </app-custom-dialog>

      <!-- AppLoading Overlay -->
      <app-loading :overlay="overlay" />
    </section>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { expenseCategory } from "@/utils/head";
import AppCardTitle from "@/components/expense/card-title";
import TTextField from "@/components/global/TextField";
import { expenseCatValidate } from "@/validations/shop";
import { success, confirm, toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";

const defaultExpenseCategoryForm = () => ({
  name: "",
});

export default {
  mixins: [expenseCatValidate, myMixin],
  components: {
    AppCardTitle,
    TTextField,
  },
  data: () => ({
    update: false,
    dialogAdd: false,
    dialog: false,
    overlay: false,
    isLoading: false,
    loadingSummary: false,
    submitting: false,
    search: "",
    page: 1,
    selectedMonth: new Date().toISOString().substr(0, 7),
    summaries: [],
    chartLabels: [],
    chartSeries: [],
    breakdownItems: [],
    summaryData: {},
    form: defaultExpenseCategoryForm(),
  }),
  async created() {
    await Promise.all([this.fetchItem(1), this.fetchSummary()]);
  },
  computed: {
    kpiItems() {
      return (this.summaries || []).map((item, index) => {
        const money =
          item.title === "total_expense" || item.title === "top_expense_category";
        const tone =
          item.title === "total_expense"
            ? "error"
            : item.title === "top_expense_category"
            ? "warning"
            : null;
        return {
          key: index,
          title: item.title,
          icon: item.icon === "fal fa-arrow-up-right" ? "fal fa-arrow-up" : item.icon,
          iconColor: tone || "primary",
          tone,
          value: Number(item.total || 0),
          unit: money ? "LAK" : "",
          note: item.description
            ? this.isLocaleKey(item.description)
              ? this.$t(item.description)
              : item.description
            : "",
        };
      });
    },
    ...mapGetters({
      category: "index/category",
    }),
    headers() {
      return expenseCategory(this);
    },
    titleStatus() {
      return this.update ? "update" : "new_data";
    },
    categoryItems() {
      if (!this.category) return [];
      if (Array.isArray(this.category)) return this.category;
      if (this.category.data && Array.isArray(this.category.data)) {
        return this.category.data;
      }
      return [];
    },
    categoryPagination() {
      if (this.category && this.category.current_page) {
        return {
          current_page: this.category.current_page,
          last_page: this.category.last_page,
          per_page: this.category.per_page,
          total: this.category.total,
        };
      }
      return {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
      };
    },
    currentMonthDisplay() {
      if (!this.selectedMonth || this.selectedMonth === "all") {
        return this.$t("all");
      }
      const parts = this.selectedMonth.split("-");
      if (parts.length === 2) {
        return `${parts[1]}/${parts[0]}`;
      }
      return this.selectedMonth;
    },
    chartColors() {
      return [
        "#5b39c9",
        "#059669",
        "#d97706",
        "#dc2626",
        "#2563eb",
        "#7c3aed",
        "#0891b2",
        "#db2777",
        "#4b5563",
        "#ea580c",
      ];
    },
    donutChartOptions() {
      const labels = this.chartLabels || [];
      const totalExpense = this.summaryData.total_expense || 0;
      const totalLabel = this.$t("total_expense");

      return {
        chart: {
          type: "donut",
          fontFamily: "inherit",
        },
        labels: labels,
        colors: this.chartColors,
        dataLabels: {
          enabled: false,
        },
        plotOptions: {
          pie: {
            donut: {
              size: "68%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "13px",
                  fontWeight: 600,
                  offsetY: -6,
                },
                value: {
                  show: true,
                  fontSize: "16px",
                  fontWeight: 700,
                  offsetY: 4,
                  formatter: (val) => Number(val).toLocaleString() + " LAK",
                },
                total: {
                  show: true,
                  label: totalLabel,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#64748b",
                  formatter: () => {
                    return Number(totalExpense).toLocaleString() + " LAK";
                  },
                },
              },
            },
          },
        },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "12px",
          markers: {
            radius: 12,
          },
        },
        tooltip: {
          y: {
            formatter: (val) => Number(val).toLocaleString() + " LAK",
          },
        },
      };
    },
  },
  methods: {
    ...mapActions({
      fetch: "index/fetch",
    }),
    getChartColor(idx) {
      return this.chartColors[idx % this.chartColors.length];
    },
    isLocaleKey(key) {
      const standardKeys = [
        "all_categories",
        "monthly_total_expense",
        "transaction_records",
        "total_categories",
        "total_expense",
        "top_expense_category",
        "expense_count",
      ];
      return standardKeys.includes(key);
    },
    async filterMonth(val) {
      this.selectedMonth = val;
      this.overlay = true;
      await Promise.all([this.fetchItem(1), this.fetchSummary()]);
      this.overlay = false;
    },
    async fetchSummary() {
      this.loadingSummary = true;
      try {
        const { data } = await this.$http.get(
          `get_summary_expense_categories?month=${this.selectedMonth || ""}`
        );
        if (data && data.status && data.data) {
          this.summaryData = data.data;
          this.summaries = data.data.summaries || [];
          this.chartLabels = data.data.chart?.labels || [];
          this.chartSeries = data.data.chart?.series || [];
          this.breakdownItems = data.data.breakdown || [];
        }
      } catch (e) {
        console.error("fetchSummary error", e);
      } finally {
        this.loadingSummary = false;
      }
    },
    async fetchItem(page = 1) {
      this.isLoading = true;
      this.page = page;
      try {
        const body = {
          that: this,
          url: `expense_category_paginate?page=${page}&q=${this.search}&month=${this.selectedMonth || ""}`,
          commit: "SET_CATEGORY",
        };
        await this.fetch(body);
      } catch (e) {
        console.error("fetchItem error", e);
      } finally {
        this.isLoading = false;
      }
    },
    async onPageChange(page) {
      await this.fetchItem(page);
    },
    newItem() {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.form = defaultExpenseCategoryForm();
      this.update = false;
      this.dialogAdd = true;
    },
    editItem(item) {
      if (this.$v && this.$v.form) {
        this.$v.form.$reset();
      }
      this.update = true;
      this.form = {
        ...defaultExpenseCategoryForm(),
        ...item,
      };
      this.dialogAdd = true;
    },
    async deleteItem({ id }) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        try {
          const { data } = await this.$http.delete(`expense_category/${id}`);
          if (data && data.status) {
            await Promise.all([
              this.fetchItem(this.categoryPagination.current_page || 1),
              this.fetchSummary(),
            ]);
            await success(this, data.message);
          } else {
            await toast(this, data?.message || "error_occurred", "error");
          }
        } catch (error) {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("error_occurred") ||
            "Error";
          if (this.$toast) {
            this.$toast.error(message);
          } else {
            await toast(this, message, "error");
          }
        }
      }
    },
    viewItem(item) {
      this.form = {
        ...defaultExpenseCategoryForm(),
        ...item,
      };
      this.dialog = true;
    },
    async submitForm() {
      try {
        this.$v.form.$touch();
        if (this.$v.form.$invalid) {
          return;
        }

        this.submitting = true;
        let result;
        if (this.update) {
          result = await this.$http.put(
            `expense_category/${this.form.id}`,
            this.form
          );
        } else {
          result = await this.$http.post("expense_category", this.form);
        }
        if (result && result.data && result.data.status) {
          this.dialogAdd = false;
          await Promise.all([
            this.fetchItem(this.categoryPagination.current_page || 1),
            this.fetchSummary(),
          ]);
          await success(this, result.data.message);
        } else {
          await toast(this, result?.data?.message || "error_occurred", "error");
        }
      } catch (error) {
        console.error("submitForm error", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("error_occurred") ||
          "Error";
        if (this.$toast) {
          this.$toast.error(message);
        } else {
          await toast(this, message, "error");
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.exp-page {
  background: #f6f6fa;
  min-height: calc(100vh - 64px);
}

.exp-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Main Panel */
.exp-main-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}

/* Toolbar Header */
.exp-toolbar {
  gap: 12px;
}

.exp-title-accent {
  width: 4px;
  height: 24px;
  background: #5b39c9;
  border-radius: 4px;
}

.exp-page-title {
  font-size: 18px;
  font-weight: 800;
  color: #141021;
  line-height: 1.2;
}

.exp-item-count {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
}

/* Toolbar Buttons */
.exp-search-box {
  min-width: 200px;
  max-width: 260px;
}

.exp-btn-secondary {
  height: 38px !important;
  border-radius: 8px !important;
  border-color: #e2e8f0 !important;
  color: #475569 !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
}

.exp-btn-secondary:hover {
  border-color: #5b39c9 !important;
  color: #5b39c9 !important;
}

.exp-btn-primary {
  height: 38px !important;
  border-radius: 8px !important;
  background: #5b39c9 !important;
  color: #ffffff !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 6px rgba(91, 57, 201, 0.25) !important;
}

.exp-btn-primary:hover {
  background: #4a2cb5 !important;
}

/* Chart and Breakdown Styles */
.exp-chart-wrapper {
  max-width: 420px;
}

.exp-breakdown-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}

.exp-breakdown-list {
  max-height: 280px;
  overflow-y: auto;
}

.exp-breakdown-row {
  background: #ffffff;
  border: 1px solid #edf2f7;
  transition: all 0.15s ease;
}

.exp-breakdown-row:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.exp-rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  font-size: 11px;
}

.exp-breakdown-name {
  font-size: 13px;
  color: #1e293b;
}

/* Price Badges */
.exp-price-badge {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.exp-price-badge--expense {
  color: #dc2626;
}

.exp-price-currency {
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  margin-left: 2px;
}

/* Table Styling */
.exp-table ::v-deep th {
  background: #f8fafc !important;
  color: #64748b !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 10px 16px !important;
}

.exp-table ::v-deep td {
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 12px 16px !important;
  font-size: 13px !important;
  color: #141021;
}

.exp-table ::v-deep tr:hover td {
  background: #faf8ff !important;
}

.exp-col-index {
  color: #94a3b8;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.exp-item-name {
  font-size: 13.5px;
  color: #141021;
}

/* =========================================
   DARK MODE OVERRIDES
========================================= */
.theme--dark.exp-page,
.theme--dark .exp-page {
  background: transparent !important;
}

.theme--dark .exp-main-card {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .exp-page-title {
  color: #ffffff;
}

.theme--dark .exp-item-count {
  color: #94a3b8;
}

.theme--dark .exp-btn-secondary {
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #cbd5e1 !important;
}

.theme--dark .exp-btn-secondary:hover {
  border-color: #818cf8 !important;
  color: #c4b5fd !important;
}

.theme--dark .exp-breakdown-panel {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .exp-breakdown-row {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .exp-breakdown-row:hover {
  border-color: rgba(154, 140, 240, 0.4);
}

.theme--dark .exp-breakdown-name,
.theme--dark .exp-item-name {
  color: #ffffff;
}

.theme--dark .exp-price-badge--expense {
  color: #f87171;
}

.theme--dark .exp-table ::v-deep th {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
  color: #94a3b8 !important;
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .exp-table ::v-deep td {
  border-bottom-color: rgba(255, 255, 255, 0.06) !important;
  color: #f1f5f9 !important;
}

.theme--dark .exp-table ::v-deep tr:hover td {
  background: rgba(255, 255, 255, 0.03) !important;
}
</style>
